// Spreading one advance figure across the requisition lines it relates to.
//
// This is a deliberate mirror of backend/app/services/advance_allocation.py.
// The server recomputes every allocation and rejects the save on any
// mismatch over a kobo, so the two files must agree exactly — same rounding,
// same residual rule, same treatment of lines with no headroom. If you
// change one, change the other and re-run both test batteries.
//
// Money is handled in integer kobo internally to sidestep binary floating
// point: 0.1 + 0.2 !== 0.3 in JavaScript, and an advance that is a kobo out
// is an advance the server refuses to save.

export const PRO_RATA = 'pro_rata';
export const EQUAL_SPLIT = 'equal_split';
export const SINGLE_LINE = 'single_line';
export const MANUAL = 'manual';

export const ALLOCATION_BASIS_OPTIONS = [
  { value: PRO_RATA, label: 'Pro-Rata by Line Value' },
  { value: MANUAL, label: 'Manual Line Allocation' },
  { value: SINGLE_LINE, label: 'Single-Line Allocation' },
  { value: EQUAL_SPLIT, label: 'Equal Split' },
];

export const RECOVERY_METHOD_OPTIONS = [
  { value: 'full_next_payment', label: 'Full recovery on next payment' },
  { value: 'pro_rata', label: 'Pro-rata across remaining payments' },
  { value: 'manual', label: 'Manual — decided at recovery time' },
];

export const INPUT_MODE_AMOUNT = 'amount';
export const INPUT_MODE_PERCENT = 'percent';

const toKobo = (v) => Math.round((Number(v) || 0) * 100);
const toNaira = (k) => k / 100;

/** Each line's remaining advance headroom. Deliveries and advances draw on
 *  the same approved value, so both are subtracted. Never negative. */
export const eligibleAdvance = (approvedValue, processedValue, advancedValue) => {
  const remaining = toKobo(approvedValue) - toKobo(processedValue) - toKobo(advancedValue);
  return remaining > 0 ? toNaira(remaining) : 0;
};

/** Push the rounding remainder onto the last line holding a non-zero share,
 *  so the allocations sum to the advance exactly. Mirrors _settle_residual. */
const settleResidual = (sharesKobo, targetKobo) => {
  const sum = sharesKobo.reduce((a, b) => a + b, 0);
  const residual = targetKobo - sum;
  if (residual === 0) return sharesKobo;
  for (let i = sharesKobo.length - 1; i >= 0; i -= 1) {
    if (sharesKobo[i] > 0) { sharesKobo[i] += residual; return sharesKobo; }
  }
  return sharesKobo;
};

/**
 * Returns one allocation per entry in `eligibleValues`, summing to exactly
 * `advanceAmount`. A line with no headroom gets nothing, whatever the basis.
 */
export const allocate = (advanceAmount, basis, eligibleValues, { manualValues, singleLineIndex } = {}) => {
  const n = eligibleValues.length;
  if (n === 0) return [];

  const targetKobo = toKobo(advanceAmount);
  const eligible = eligibleValues.map(v => Math.max(0, toKobo(v)));

  if (basis === MANUAL) {
    return Array.from({ length: n }, (_, i) => toNaira(toKobo((manualValues || [])[i] || 0)));
  }

  if (basis === SINGLE_LINE) {
    let target = singleLineIndex;
    if (target == null || target < 0 || target >= n) {
      const firstPayable = eligible.findIndex(v => v > 0);
      target = firstPayable === -1 ? 0 : firstPayable;
    }
    return Array.from({ length: n }, (_, i) => (i === target ? toNaira(targetKobo) : 0));
  }

  if (basis === EQUAL_SPLIT) {
    const payable = eligible.map((v, i) => (v > 0 ? i : -1)).filter(i => i !== -1);
    if (payable.length === 0) return Array(n).fill(0);
    const share = Math.round(targetKobo / payable.length);
    const shares = Array.from({ length: n }, (_, i) => (payable.includes(i) ? share : 0));
    return settleResidual(shares, targetKobo).map(toNaira);
  }

  if (basis === PRO_RATA) {
    const totalEligible = eligible.reduce((a, b) => a + b, 0);
    if (totalEligible <= 0) return Array(n).fill(0);
    const shares = eligible.map(v => Math.round((targetKobo * v) / totalEligible));
    return settleResidual(shares, targetKobo).map(toNaira);
  }

  throw new Error(`Unknown allocation basis: ${basis}`);
};

/** The four figures shown above the allocation table. */
export const advanceTotals = (selectedLines) => {
  const sum = (fn) => toNaira(selectedLines.reduce((acc, l) => acc + toKobo(fn(l)), 0));
  const eligiblePurchaseValue = sum(l => l.approved_value);
  const previousAdvancePaid = sum(l => l.previously_advanced_value);
  const processedValue = sum(l => l.processed_value);
  const remainingEligible = Math.max(
    0,
    toNaira(toKobo(eligiblePurchaseValue) - toKobo(previousAdvancePaid) - toKobo(processedValue)),
  );
  return { eligiblePurchaseValue, previousAdvancePaid, processedValue, remainingEligible };
};

/** Blocking problems, shown in the panel exactly as the server would report
 *  them. The server checks all of these again — this is for fast feedback,
 *  never the authority. */
export const advanceIssues = ({ advanceAmount, reason, selectedLines, allocations, remainingEligible }) => {
  const issues = [];
  if (!(Number(advanceAmount) > 0)) issues.push('Advance amount must be greater than zero.');
  if (!selectedLines.length) issues.push('Select at least one approved purchase line for the advance to relate to.');
  if (!(reason || '').trim()) issues.push('Advance Payment Reason is required.');

  const allocated = toKobo((allocations || []).reduce((a, b) => a + (Number(b) || 0), 0));
  if (selectedLines.length && Math.abs(allocated - toKobo(advanceAmount)) > 1) {
    issues.push(`Allocations add up to ₦${toNaira(allocated).toLocaleString()} but the advance is ₦${(Number(advanceAmount) || 0).toLocaleString()}.`);
  }
  (allocations || []).forEach((amt, i) => {
    const line = selectedLines[i];
    if (line && toKobo(amt) > toKobo(line.eligible_advance_value) + 1) {
      issues.push(`Line ${i + 1}: only ₦${Number(line.eligible_advance_value).toLocaleString()} of its approved value is still un-advanced.`);
    }
  });
  if (toKobo(advanceAmount) > toKobo(remainingEligible) + 1) {
    issues.push('Advance exceeds the remaining eligible purchase value.');
  }
  return issues;
};
